import React, { useEffect, useState } from "react";
import { Link, Navigate, useLocation } from "react-router-dom";
import {
  CheckCircle2,
  Circle,
  AlertCircle,
  UserRound,
  FileText,
} from "lucide-react";
import SalesAffiliateLayout from "../components/SalesAffiliateLayout";
import SalesAffiliateStatusBanner from "../components/SalesAffiliateStatusBanner";
import SalesAffiliateTrainingSection from "../components/SalesAffiliateTrainingSection";
import SalesAffiliatePaymentSection from "../components/SalesAffiliatePaymentSection";
import SalesAffiliateActivationGuide from "../components/SalesAffiliateActivationGuide";
import { useSalesAffiliateWorkspace } from "../context/SalesAffiliateWorkspaceContext";
import { supabase } from "../lib/supabaseClient";

const CONFIRM_FUNCTION_NAME = "sales_affiliate_profile_confirmation";

const PAYMENT_VALIDATION_CODES = new Set([
  "STRIPE_ONBOARDING_DETAILS_NOT_SUBMITTED",
  "STRIPE_SETUP_VALIDATION_REQUIRED",
]);

function paymentNeedsValidation(requirement) {
  return PAYMENT_VALIDATION_CODES.has(
    String(requirement?.action_required_code || "").toUpperCase()
  );
}

function LoadingState() {
  return (
    <div className="flex min-h-[420px] items-center justify-center">
      <div className="rounded-2xl border border-slate-200 bg-white px-5 py-4 text-sm font-semibold text-slate-700 shadow-sm">
        Loading onboarding…
      </div>
    </div>
  );
}

function RequirementIcon({ complete, status }) {
  if (complete) {
    return <CheckCircle2 className="text-emerald-600" size={26} />;
  }

  if (status === "ACTION_REQUIRED" || status === "FAILED") {
    return <AlertCircle className="text-amber-600" size={26} />;
  }

  return <Circle className="text-slate-300" size={26} />;
}

function PaymentRequirementIcon({ requirement }) {
  const presentationState = requirement?.presentation_state || "NOT_STARTED";
  const allowedAction = requirement?.allowed_action || "";
  const validationRequired = paymentNeedsValidation(requirement);

  if (presentationState === "VERIFIED") {
    return <CheckCircle2 className="flex-none text-emerald-600" size={28} />;
  }

  if (validationRequired) {
    return <AlertCircle className="flex-none text-blue-600" size={32} />;
  }

  if (
    presentationState === "ACTION_REQUIRED" ||
    presentationState === "FAILED"
  ) {
    return <AlertCircle className="flex-none text-amber-600" size={34} />;
  }

  if (
    presentationState === "SUBMITTED_PENDING_VERIFICATION" ||
    presentationState === "IN_PROGRESS" ||
    allowedAction === "RESUME_WORKFLOW"
  ) {
    return <Circle className="flex-none text-blue-600" size={28} />;
  }

  return <Circle className="flex-none text-slate-400" size={28} />;
}

function PaymentRequirementStatus({ requirement }) {
  const presentationState = requirement?.presentation_state || "NOT_STARTED";
  const allowedAction = requirement?.allowed_action || "";
  const validationRequired = paymentNeedsValidation(requirement);

  if (presentationState === "VERIFIED") {
    return (
      <div className="mt-1 text-sm font-black text-emerald-700">
        Payment setup verified
      </div>
    );
  }

  if (validationRequired) {
    return (
      <div
        role="status"
        className="mt-2 rounded-xl border border-blue-300 bg-blue-50 px-4 py-3"
      >
        <div className="text-base font-black text-blue-950">
          Stripe account connected · Validation required
        </div>
        <div className="mt-1 text-sm font-bold leading-5 text-blue-800">
          Return to Stripe to finish or confirm your setup before payouts can be enabled.
        </div>
      </div>
    );
  }

  if (
    presentationState === "ACTION_REQUIRED" ||
    presentationState === "FAILED"
  ) {
    return (
      <div
        role="status"
        className="mt-2 rounded-xl border border-amber-300 bg-amber-50 px-4 py-3"
      >
        <div className="text-base font-black text-amber-900">
          Payment setup needs attention
        </div>
        <div className="mt-1 text-sm font-bold leading-5 text-amber-800">
          Please complete the requested payment-provider steps to ensure payouts.
        </div>
      </div>
    );
  }

  if (presentationState === "SUBMITTED_PENDING_VERIFICATION") {
    return (
      <div className="mt-1 text-sm font-bold text-blue-700">
        Verification pending
      </div>
    );
  }

  if (
    presentationState === "IN_PROGRESS" ||
    allowedAction === "RESUME_WORKFLOW"
  ) {
    return (
      <div className="mt-1 text-sm font-bold text-blue-700">
        Payment setup in progress
      </div>
    );
  }

  return (
    <div className="mt-1 text-sm font-semibold text-slate-600">
      Optional for activation · Not set up
    </div>
  );
}

export default function SalesAffiliateOnboarding() {
  const {
    loading,
    statusLoadFailed,
    errorMessage,
    workspaceData: status,
    configuration,
    refreshWorkspace,
  } = useSalesAffiliateWorkspace();

  const [confirming, setConfirming] = useState(false);
  const [confirmError, setConfirmError] = useState("");
  const location = useLocation();

  useEffect(() => {
    if (
      loading ||
      statusLoadFailed ||
      !status?.affiliate_exists ||
      !location.hash
    ) {
      return undefined;
    }

    const targetId = decodeURIComponent(location.hash.slice(1));
    const frame = window.requestAnimationFrame(() => {
      document.getElementById(targetId)?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    });

    return () => window.cancelAnimationFrame(frame);
  }, [loading, location.hash, status?.affiliate_exists, statusLoadFailed]);

  const handleConfirmProfile = async () => {
    if (confirming) return;

    setConfirmError("");
    setConfirming(true);

    try {
      const { data, error } = await supabase.functions.invoke(
        CONFIRM_FUNCTION_NAME,
        { body: {} }
      );

      if (error) {
        throw new Error(error.message || "Profile confirmation failed.");
      }

      if (!data?.ok) {
        throw new Error(data?.error || "Profile confirmation failed.");
      }

      await refreshWorkspace();
    } catch (err) {
      setConfirmError(err?.message || "Profile confirmation failed.");
    } finally {
      setConfirming(false);
    }
  };

  if (loading) {
    return (
      <SalesAffiliateLayout activeSection="onboarding-overview">
        <LoadingState />
      </SalesAffiliateLayout>
    );
  }

  if (statusLoadFailed) {
    return (
      <SalesAffiliateLayout activeSection="onboarding-overview">
        <div className="mx-auto flex min-h-[500px] max-w-2xl items-center px-8">
          <SalesAffiliateStatusBanner
            tone="warning"
            eyebrow="Workspace unavailable"
            title="We could not load your onboarding status"
          >
            <p>{errorMessage || "Please retry the status check."}</p>
            <button
              type="button"
              onClick={refreshWorkspace}
              className="mt-5 rounded-2xl bg-slate-950 px-5 py-3 text-sm font-black text-white"
            >
              Retry status check
            </button>
          </SalesAffiliateStatusBanner>
        </div>
      </SalesAffiliateLayout>
    );
  }

  if (!configuration.allowsOnboardingPage) {
    return <Navigate to={configuration.defaultPath} replace />;
  }

  if (!status?.affiliate_exists) {
    return <Navigate to="/sales-affiliate/application" replace />;
  }

  const requirements = status.requirements || [];
  const progress = status.progress || { completed: 0, total: 4, percent: 0 };
  const nextAction = status.next_action;
  const isActive = status.lifecycle_state === "ACTIVE";
  const profileRequirement = requirements.find((item) => item.key === "profile");
  const profileComplete = Boolean(profileRequirement?.complete);
  const agreementRequirement = requirements.find(
    (item) => item.key === "agreement"
  );
  const agreementComplete = Boolean(agreementRequirement?.complete);
  const trainingRequirement = requirements.find(
    (item) => item.key === "training"
  );
  const paymentRequirement = requirements.find(
    (item) => item.key === "payment"
  );
  const agreement = status.agreement || {};
  const agreementAvailable = Boolean(agreement.available);
  const lifecycleState = status.lifecycle_state || "PRE_ACTIVATION";

  // ACTIVE affiliates should not see the full incomplete onboarding experience.
  // No separate active-workspace route exists yet, so render a compact
  // activation-complete state with an explicit account-entry action.
  if (isActive && location.hash !== "#payment") {
    return (
      <SalesAffiliateLayout
        activeSection="activation-summary"
        lifecycleState={lifecycleState}
      >
        <div className="mx-auto max-w-3xl px-8 py-16">
          <div className="rounded-3xl border border-emerald-200 bg-white px-8 py-10 shadow-sm">
            <div className="flex items-center gap-3">
              <CheckCircle2 className="text-emerald-600" size={30} />
              <h1 className="text-3xl font-black tracking-tight text-slate-950">
                Activation complete
              </h1>
            </div>
            <p className="mt-4 text-sm leading-6 text-slate-600">
              Your Sales Affiliate account is active. All activation
              requirements have been satisfied.
            </p>

            <dl className="mt-6 grid gap-4 sm:grid-cols-2">
              <div className="rounded-2xl bg-slate-50 px-4 py-4">
                <dt className="text-xs font-black uppercase tracking-[0.18em] text-slate-400">
                  Name
                </dt>
                <dd className="mt-2 text-sm font-bold text-slate-800">
                  {status.affiliate?.full_name || "Not provided"}
                </dd>
              </div>
              <div className="rounded-2xl bg-slate-50 px-4 py-4">
                <dt className="text-xs font-black uppercase tracking-[0.18em] text-slate-400">
                  Email
                </dt>
                <dd className="mt-2 break-all text-sm font-bold text-slate-800">
                  {status.affiliate?.email || "Not provided"}
                </dd>
              </div>
            </dl>

            <Link
              to="/sales-affiliate/dashboard"
              className="mt-8 inline-flex items-center rounded-2xl bg-slate-950 px-5 py-3 text-sm font-black text-white shadow-sm hover:bg-slate-800"
            >
              Go to your dashboard
            </Link>
          </div>

          <SalesAffiliateActivationGuide
            lifecycleState={lifecycleState}
            activationGuide={status.activation_guide || null}
            refreshWorkspace={refreshWorkspace}
          />
        </div>
      </SalesAffiliateLayout>
    );
  }

  return (
    <SalesAffiliateLayout
        activeSection="onboarding-overview"
        lifecycleState={lifecycleState}
      >
      <div className="mx-auto max-w-6xl px-8 py-10">
        <div className="flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <div className="text-xs font-black uppercase tracking-[0.32em] text-blue-600">
              Sales Affiliate Activation
            </div>
            <h1 className="mt-3 text-4xl font-black tracking-tight text-slate-950">
              {isActive ? "Activation complete" : "Complete your onboarding"}
            </h1>
            <p className="mt-3 max-w-3xl text-sm leading-6 text-slate-600">
              {isActive
                ? "Your Sales Affiliate account is active."
                : "Complete the requirements below before your Sales Affiliate account can be activated."}
            </p>
          </div>

          <div className="rounded-2xl border border-slate-200 bg-white px-5 py-4 shadow-sm">
            <div className="text-xs font-black uppercase tracking-[0.2em] text-slate-400">
              Activation progress
            </div>
            <div className="mt-2 text-2xl font-black text-slate-950">
              {progress.completed} of {progress.total} complete
            </div>
          </div>
        </div>

        {errorMessage ? (
          <div className="mt-8 rounded-2xl border border-red-200 bg-red-50 px-5 py-4 text-sm font-semibold text-red-700">
            {errorMessage}
          </div>
        ) : null}

        <div className="mt-8 overflow-hidden rounded-full bg-slate-200">
          <div
            className="h-3 rounded-full bg-blue-600 transition-all"
            style={{ width: `${progress.percent || 0}%` }}
          />
        </div>

        <SalesAffiliateActivationGuide
          lifecycleState={lifecycleState}
          activationGuide={status.activation_guide || null}
          refreshWorkspace={refreshWorkspace}
        />

        {nextAction ? (
          <div className="mt-8">
            <SalesAffiliateStatusBanner
              tone="info"
              eyebrow="Next required action"
              title={nextAction.label}
            >
              <p>
                {nextAction.detail ||
                  "Complete this requirement to continue your activation."}
              </p>
              {nextAction.key === "profile" ? (
                <a
                  href="#account"
                  className="mt-4 inline-flex items-center justify-center rounded-2xl bg-slate-950 px-5 py-3 text-sm font-black text-white shadow-sm hover:bg-slate-800"
                >
                  Review & Confirm Profile
                </a>
              ) : nextAction.key === "agreement" && agreementAvailable ? (
                <Link
                  to="/sales-affiliate/agreement"
                  className="mt-4 inline-flex items-center justify-center rounded-2xl bg-slate-950 px-5 py-3 text-sm font-black text-white shadow-sm hover:bg-slate-800"
                >
                  Review Agreement
                </Link>
              ) : nextAction.key === "training" ? (
                <a
                  href="#training"
                  className="mt-4 inline-flex items-center justify-center rounded-2xl bg-slate-950 px-5 py-3 text-sm font-black text-white shadow-sm hover:bg-slate-800"
                >
                  Go to Training
                </a>
              ) : null}
            </SalesAffiliateStatusBanner>
          </div>
        ) : null}

        <div id="checklist" className="mt-8 space-y-4">
          {requirements.map((requirement) => (
            <div
              key={requirement.key}
              className="flex flex-col gap-5 rounded-3xl border border-slate-200 bg-white px-6 py-5 shadow-sm sm:flex-row sm:items-center sm:justify-between"
            >
              <div className="flex items-start gap-4">
                {requirement.key === "payment" ? (
                  <PaymentRequirementIcon requirement={requirement} />
                ) : (
                  <RequirementIcon
                    complete={requirement.complete}
                    status={requirement.status}
                  />
                )}
                <div>
                  <div className="text-lg font-black text-slate-950">
                    {requirement.label}
                  </div>
                  {requirement.key === "payment" ? (
                    <PaymentRequirementStatus requirement={requirement} />
                  ) : (
                    <div className="mt-1 text-sm font-semibold text-slate-500">
                      {requirement.complete
                        ? "Complete"
                        : requirement.status === "NOT_ASSIGNED"
                        ? "Awaiting assignment"
                        : requirement.status === "IN_PROGRESS"
                        ? "In progress"
                        : requirement.status === "ACTION_REQUIRED"
                        ? "Action required"
                        : requirement.status === "NOT_CONFIGURED"
                        ? "Awaiting configuration"
                        : requirement.status === "FAILED"
                        ? "Needs attention"
                        : "Not complete"}
                    </div>
                  )}
                </div>
              </div>

              <div className="flex w-full max-w-sm flex-col gap-3 sm:items-end">
                <div className="w-full rounded-2xl bg-slate-50 px-4 py-3 text-sm leading-6 text-slate-600">
                  {requirement.detail}
                </div>

                {requirement.key === "profile" && !requirement.complete ? (
                  <a
                    href="#account"
                    className="inline-flex items-center justify-center rounded-2xl bg-slate-950 px-5 py-3 text-sm font-black text-white shadow-sm hover:bg-slate-800"
                  >
                    Review & Confirm Profile
                  </a>
                ) : requirement.key === "agreement" && agreementAvailable ? (
                  <Link
                    to="/sales-affiliate/agreement"
                    className={`inline-flex items-center justify-center rounded-2xl px-5 py-3 text-sm font-black shadow-sm ${
                      agreementComplete
                        ? "border border-slate-300 bg-white text-slate-900 hover:bg-slate-100"
                        : "bg-slate-950 text-white hover:bg-slate-800"
                    }`}
                  >
                    {agreementComplete ? "View Agreement" : "Review Agreement"}
                  </Link>
                ) : requirement.key === "training" && !requirement.complete ? (
                  <a
                    href="#training"
                    className="inline-flex items-center justify-center rounded-2xl bg-slate-950 px-5 py-3 text-sm font-black text-white shadow-sm hover:bg-slate-800"
                  >
                    Go to Training
                  </a>
                ) : null}
              </div>
            </div>
          ))}
        </div>

        <section
          id="account"
          className="mt-8 rounded-3xl border border-slate-200 bg-white px-6 py-6 shadow-sm"
        >
          <div className="flex items-center gap-3">
            <UserRound className="text-blue-600" size={24} />
            <h2 className="text-xl font-black text-slate-950">Account</h2>
          </div>

          <div className="mt-5 grid gap-4 sm:grid-cols-2">
            <div className="rounded-2xl bg-slate-50 px-4 py-4">
              <div className="text-xs font-black uppercase tracking-[0.18em] text-slate-400">
                Name
              </div>
              <div className="mt-2 text-sm font-bold text-slate-800">
                {status.affiliate?.full_name || "Not provided"}
              </div>
            </div>
            <div className="rounded-2xl bg-slate-50 px-4 py-4">
              <div className="text-xs font-black uppercase tracking-[0.18em] text-slate-400">
                Email
              </div>
              <div className="mt-2 break-all text-sm font-bold text-slate-800">
                {status.affiliate?.email || "Not provided"}
              </div>
            </div>
          </div>

          <div className="mt-6 rounded-2xl border border-slate-200 bg-slate-50 px-5 py-5">
            {profileComplete ? (
              <div className="flex items-start gap-3">
                <CheckCircle2 className="mt-0.5 flex-none text-emerald-600" size={22} />
                <div>
                  <div className="text-sm font-black text-slate-900">
                    Profile confirmed
                  </div>
                  <div className="mt-1 text-sm leading-6 text-slate-600">
                    You have confirmed that the name and email above are accurate.
                  </div>
                </div>
              </div>
            ) : (
              <div>
                <div className="text-sm font-black text-slate-900">
                  Confirm your profile
                </div>
                <p className="mt-1 text-sm leading-6 text-slate-600">
                  Please confirm that the name and email shown above are accurate.
                  This is required before your Sales Affiliate account can be
                  activated.
                </p>

                {confirmError ? (
                  <div
                    role="alert"
                    className="mt-4 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-semibold text-red-700"
                  >
                    {confirmError}
                  </div>
                ) : null}

                <button
                  type="button"
                  onClick={handleConfirmProfile}
                  disabled={confirming}
                  className="mt-4 rounded-2xl bg-slate-950 px-5 py-3 text-sm font-black text-white shadow-sm hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {confirming
                    ? "Confirming..."
                    : confirmError
                    ? "Retry confirmation"
                    : "Confirm my profile is accurate"}
                </button>
              </div>
            )}
          </div>
        </section>

        <section
          id="agreement"
          className="mt-8 rounded-3xl border border-slate-200 bg-white px-6 py-6 shadow-sm"
        >
          <div className="flex items-center gap-3">
            <FileText className="text-blue-600" size={24} />
            <h2 className="text-xl font-black text-slate-950">
              Sales Affiliate Agreement
            </h2>
          </div>

          <div className="mt-5 rounded-2xl border border-slate-200 bg-slate-50 px-5 py-5">
            {agreementComplete ? (
              <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                <div className="flex items-start gap-3">
                  <CheckCircle2 className="mt-0.5 flex-none text-emerald-600" size={22} />
                  <div>
                    <div className="text-sm font-black text-slate-900">Agreement accepted</div>
                    <div className="mt-1 text-sm leading-6 text-slate-600">
                      Version {agreement.version || "—"}
                      {agreement.accepted_at
                        ? ` accepted ${new Date(agreement.accepted_at).toLocaleString()}`
                        : " has been accepted"}.
                    </div>
                  </div>
                </div>
                <Link
                  to="/sales-affiliate/agreement"
                  className="inline-flex items-center justify-center rounded-2xl border border-slate-300 bg-white px-4 py-3 text-sm font-black text-slate-900 shadow-sm hover:bg-slate-100"
                >
                  View Agreement
                </Link>
              </div>
            ) : agreementAvailable ? (
              <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <div className="text-sm font-black text-slate-900">
                    Review & Accept Sales Affiliate Agreement
                  </div>
                  <p className="mt-1 max-w-2xl text-sm leading-6 text-slate-600">
                    Review Version {agreement.version || "—"} and electronically accept it before activation.
                  </p>
                </div>
                <Link
                  to="/sales-affiliate/agreement"
                  className="inline-flex items-center justify-center rounded-2xl bg-slate-950 px-5 py-3 text-sm font-black text-white shadow-sm hover:bg-slate-800"
                >
                  Review Agreement
                </Link>
              </div>
            ) : (
              <div className="flex items-start gap-3">
                <AlertCircle className="mt-0.5 flex-none text-amber-600" size={22} />
                <div>
                  <div className="text-sm font-black text-slate-900">Sales Affiliate Agreement</div>
                  <p className="mt-1 text-sm leading-6 text-slate-600">
                    The complete required agreement is not yet available. Acceptance remains disabled until the exact agreement version is configured for review.
                  </p>
                </div>
              </div>
            )}
          </div>
        </section>

        <SalesAffiliateTrainingSection
          requirement={trainingRequirement}
          refreshWorkspace={refreshWorkspace}
        />

        <SalesAffiliatePaymentSection
          requirement={paymentRequirement}
          refreshWorkspace={refreshWorkspace}
        />

      </div>
    </SalesAffiliateLayout>
  );
}
